import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "../lib/utils";
import { Link } from "react-router-dom";

const CodeBlock = React.lazy(() => import("./CodeBlock"));

// ─── Helper: recursively extract text from React children ───
function extractText(children) {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(extractText).join("");
  if (children?.props?.children) return extractText(children.props.children);
  return "";
}

// ─── Citation tooltip component ───
function CitationTooltip({ sources, domainId, sourceIndex, children }) {
  const [open, setOpen] = useState(false);
  const [clickOpen, setClickOpen] = useState(false);
  const containerRef = useRef(null);
  const timerRef = useRef(null);

  const source = sources?.find((s) => s.sourceIndex === sourceIndex);
  if (!source) return children;

  const isOpen = clickOpen || open;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setClickOpen(false);
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleBadgeEnter = () => {
    clearTimeout(timerRef.current);
    setOpen(true);
  };

  const handleBadgeLeave = () => {
    // Give time to move mouse to popup
    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, 300);
  };

  const handlePopupEnter = () => {
    clearTimeout(timerRef.current);
    setOpen(true);
  };

  const handlePopupLeave = () => {
    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  const handleBadgeClick = (e) => {
    e.stopPropagation();
    setClickOpen((prev) => !prev);
  };

  return (
    <span className="relative inline-block" ref={containerRef}>
      <span
        className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300 cursor-pointer hover:bg-emerald-200 dark:hover:bg-emerald-800 transition-colors ml-0.5"
        onMouseEnter={handleBadgeEnter}
        onMouseLeave={handleBadgeLeave}
        onClick={handleBadgeClick}
      >
        {sourceIndex}
      </span>
      {isOpen && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-80 z-50"
          onMouseEnter={handlePopupEnter}
          onMouseLeave={handlePopupLeave}
        >
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg p-3 text-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slate-800 dark:text-slate-100">{source.documentTitle || "Document"}</span>
              <span className="text-slate-400 dark:text-slate-500">Page {source.page || "?"}</span>
            </div>
            {source.section && (
              <p className="text-slate-500 dark:text-slate-400 mb-1 truncate">Section: {source.section}</p>
            )}
            <div className="max-h-32 overflow-y-auto mb-2 bg-slate-50 dark:bg-slate-800 rounded p-2 text-slate-600 dark:text-slate-300 leading-relaxed">
              {source.content?.substring(0, 300)}{source.content?.length > 300 ? "..." : ""}
            </div>
            <div className="flex items-center justify-between">
              <Link
                to={`/domains/${domainId}/documents/${source.documentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 hover:underline font-medium inline-flex items-center gap-1"
              >
                Open document
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </Link>
              <span className="text-slate-400 dark:text-slate-500">Source #{source.sourceIndex}</span>
            </div>
          </div>
          {/* Arrow */}
          <div className="w-2 h-2 bg-white dark:bg-slate-900 border-l border-b border-slate-200 dark:border-slate-700 rotate-45 absolute left-1/2 -translate-x-1/2 -bottom-1"></div>
        </div>
      )}
    </span>
  );
}

export default function StreamingMessage({ content, sources, domainId, isTyping, className }) {
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [content]);

  // Custom components for ReactMarkdown that render citations
  const components = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || "");
      const language = match ? match[1] : "";
      return !inline ? (
        <React.Suspense
          fallback={
            <pre className="bg-slate-900 text-slate-200 p-3 rounded-md text-xs my-2 overflow-x-auto">
              <code>{children}</code>
            </pre>
          }
        >
          <CodeBlock language={language}>{children}</CodeBlock>
        </React.Suspense>
      ) : (
        <code className="bg-slate-800 px-1 py-0.5 rounded text-xs text-slate-200" {...props}>
          {children}
        </code>
      );
    },
    table({ children }) {
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">{children}</table>
        </div>
      );
    },
    thead({ children }) {
      return <thead className="bg-slate-100 dark:bg-slate-800">{children}</thead>;
    },
    th({ children }) {
      return <th className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-left text-xs font-semibold">{children}</th>;
    },
    td({ children }) {
      return <td className="border border-slate-200 dark:border-slate-700 px-2 py-1 text-xs">{children}</td>;
    },
    blockquote({ children }) {
      return (
        <blockquote className="border-l-4 border-emerald-500 pl-4 italic text-slate-600 dark:text-slate-400 my-2">
          {children}
        </blockquote>
      );
    },
    ul({ children }) {
      return <ul className="list-disc pl-5 my-1">{children}</ul>;
    },
    ol({ children }) {
      return <ol className="list-decimal pl-5 my-1">{children}</ol>;
    },
    h1({ children }) {
      return <h1 className="text-xl font-bold mt-4 mb-2">{children}</h1>;
    },
    h2({ children }) {
      return <h2 className="text-lg font-semibold mt-3 mb-1">{children}</h2>;
    },
    h3({ children }) {
      return <h3 className="text-base font-medium mt-2 mb-1">{children}</h3>;
    },
    p({ children }) {
      // Interpolate citation markers [Source N] into tooltip badges
      const text = extractText(children);
      if (!text || !text.includes("[Source")) {
        return <p className="my-1 leading-relaxed">{children}</p>;
      }
      const parts = text.split(/(\[Source\s+\d+\])/g);
      if (parts.length === 1) {
        return <p className="my-1 leading-relaxed">{children}</p>;
      }
      return (
        <p className="my-1 leading-relaxed">
          {parts.map((part, i) => {
            const match = part.match(/^\[Source\s+(\d+)\]$/);
            if (!match) return part;
            const idx = parseInt(match[1], 10);
            return (
              <CitationTooltip key={i} sources={sources} domainId={domainId} sourceIndex={idx}>
                {part}
              </CitationTooltip>
            );
          })}
        </p>
      );
    },
    a({ href, children }) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">
          {children}
        </a>
      );
    },
  };

  return (
    <div className={cn("space-y-2", className)}>
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
          {content || " "}
        </ReactMarkdown>
      </div>
      {isTyping && (
        <div className="flex gap-1 mt-2">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce" />
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce delay-75" />
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-bounce delay-150" />
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
}
